// BUILD-046 correction cycle 1bb88b3e (stage B2b, item N2): the searchable people multi-select of the Assign task dialog.
//
// Replaces the scrolling roster of checkboxes. A text input (role=combobox) filters the people by display name as you type; a dropdown listbox of the
// matches opens as you type, with the arrow keys, or with the "Show people" button (a 24px chevron in a 40px hit area); only an explicit gesture chooses: a click
// on a row, Enter on the highlighted result, a Shift range, or Tab with text typed and a name highlighted (Owner direction 2026-10-20 night; typing alone, opening
// the list and Shift+Tab never choose anyone, and Tab with nothing typed or nothing matching adds nobody). Tab is never prevented: it adds the highlighted person,
// clears the text, closes the list and focus moves on to the next field as usual. Escape closes the list. Chosen people are removable chips (each chip is a real
// button named "Remove <name>") and a person can never be chosen twice.
// RANGES (Owner edit, client-side only): a click adds that person and keeps the list open; Shift+click adds everyone between the last clicked person (the
// range anchor) and the clicked one, in the order shown; Shift+ArrowDown / ArrowUp extend the same way from the keyboard. People already chosen stay in the
// open list with a check mark (aria-selected) so a range stays contiguous. What is submitted does not change: the same chips, the same recipients.
// All rules (filtering, keys, ranges, active option, wording) are in assignModel.searchPeople / chipsFor and peopleComboboxModel; this file draws them.
//
// The same component serves the Collaborators field (the chips and the list are the same; only the label and the ids differ).
import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import type { ListView } from './listStateModel';
import { chipsFor, searchPeople } from './assignModel';
import type { PersonOption } from './assignModel';
import { afterMousePick, clampActive, closeList, decideExtend, decideKey, decidePick, describeCombo, describeTabAdd, initialCombo, optionId, placeholderFor, toggleList, typeQuery } from './peopleComboboxModel';
import type { ComboState } from './peopleComboboxModel';
// D1 display cleanup: people are shown as First name + last initial (chips and options), the department on a quiet second line in the list, and the full name on hover,
// on keyboard focus (chips) and in the accessible name (options). Search still matches the stored full name (assignModel.searchPeople).
import { disambiguationById, personDisplay } from './displayNames';
import type { PersonChoice } from './displayNames';
import { useNameTip } from './PersonName';
// Increment 1b-i: a small decorative initials circle before each person in the list (the chips keep their text names).
import { AvatarCircle } from './PersonAvatar';
import { avatarLabels } from './personAvatarModel';
import { DropdownShell } from './FloatingPanel';

/** The one short line shown in the open list. */
const RANGE_HINT = 'Click to add. Shift-click to add a range.';

/**
 * One chosen person: the short name, a Remove button named with the FULL name, and the full name as a tooltip on hover and while the Remove button has keyboard focus
 * (the chip itself is not a tab stop; its Remove button is). Escape closes the tooltip first.
 */
function ChosenChip({ name, department, tag, disabled, onRemove }: { name: string; department?: string; tag: string; disabled: boolean; onRemove: () => void }) {
  const display = personDisplay(name, { department });
  const different = display.full !== '' && display.full !== display.short;
  const tip = useNameTip(different ? `${display.full}${tag}` : null);
  return (
    <li ref={tip.setAnchor} {...tip.handlers} className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50 py-0.5 pl-2 pr-1 text-xs text-gray-900">
      <span>
        {display.short}
        {tag}
      </span>
      <button
        type="button"
        disabled={disabled}
        onClick={onRemove}
        aria-label={`Remove ${display.full}`}
        title={tip.open ? undefined : `Remove ${display.full}`}
        className="flex h-4 w-4 items-center justify-center rounded-full text-gray-600 hover:bg-sky-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4AABDE] disabled:opacity-50"
      >
        <span aria-hidden="true">&times;</span>
      </button>
      {tip.overlay}
    </li>
  );
}

/** The label of one option: the short name, and the quiet second line (department, and the full name or an identifier where two would look alike). */
function optionText(choice: PersonChoice | undefined, fallback: string): { short: string; detail: string; accessible: string } {
  if (choice === undefined) {
    const display = personDisplay(fallback);
    return { short: display.short, detail: '', accessible: display.full };
  }
  return { short: choice.short, detail: choice.detail, accessible: choice.accessibleName };
}

export interface PeopleComboboxProps {
  /** Pass F1: draw the results list on the page body against the field, so no clipping container can cut it off (the compact Edit task form). Default false: in place. */
  floating?: boolean;
  /** Base for the element ids; the input is `${id}` so a host can focus it. */
  id: string;
  /** The visible label text, shown by the host above the field; the input is named by it through `labelledBy`. */
  labelledBy: string;
  placeholder?: string;
  /** The loaded people with their list state (loading, error, empty, data). */
  people: ListView<PersonOption>;
  onRetry: () => void;
  selectedIds: readonly string[];
  disabled: boolean;
  onAdd: (employeeId: string) => void;
  onRemove: (employeeId: string) => void;
  /** The Caller, marked "(you)" in the list and on the chip. */
  callerEmployeeId?: string | null;
  /** Ids of the elements that describe the field (for example the inline problem), joined by spaces. */
  describedBy?: string;
  invalid?: boolean;
  /** The dialog puts focus here when it opens (read by useDialogFocus.focusFirst). */
  wantsFocus?: boolean;
  /** A small icon at the left end of the field ('search' for the Collaborators field). Decorative: the field is named by its label. */
  leadingIcon?: 'search';
  /** The field is required (aria-required on the input). */
  required?: boolean;
}

export function PeopleCombobox({ floating = false, id, labelledBy, placeholder = 'Type a name or press the down arrow', people, onRetry, selectedIds, disabled, onAdd, onRemove, callerEmployeeId = null, describedBy, invalid = false, wantsFocus = false, leadingIcon, required = false }: PeopleComboboxProps) {
  const [combo, setCombo] = useState<ComboState>(initialCombo());
  const listboxId = `${id}-listbox`;
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listOf: readonly PersonOption[] = people.kind === 'data' ? people.items : [];
  const options = searchPeople(listOf, combo.query);
  const chips = chipsFor(selectedIds, listOf);
  const view = describeCombo(combo, options, id, people.kind === 'loading');
  const youTag = (employeeId: string) => (callerEmployeeId !== null && employeeId === callerEmployeeId ? ' (you)' : '');
  // How each person is labelled, judged over the whole roster (not just the rows a search leaves), so a label does not change as you type.
  const choiceById = disambiguationById(listOf);
  // The circles are labelled over the same whole roster, so they are unique in the list however it is filtered and do not change as you type; the colour is the person's (the employee id).
  const roster = avatarLabels(listOf.map((person) => ({ id: person.employeeId, name: person.displayName, department: person.department })));
  const circleById = new Map(listOf.map((person, index) => [person.employeeId, roster[index]] as const));
  const chosen = new Set(selectedIds);
  const hintId = `${id}-hint`;
  // The range anchor: the person last clicked (or the row a Shift+Arrow range started from). It only lives while the list is open.
  const [anchor, setAnchor] = useState<string | null>(null);
  useEffect(() => {
    if (!combo.open) setAnchor(null);
  }, [combo.open]);
  // What Tab just added ("Alice Accounting added"), said once in the polite status line because focus has already moved on and the list is closed. It goes
  // as soon as the list opens again (the usual "N people found" line then takes the same place).
  const [announcement, setAnnouncement] = useState('');
  useEffect(() => {
    if (combo.open) setAnnouncement('');
  }, [combo.open]);
  // Text typed and a name highlighted that is not chosen yet: Tab would add that person, so the hint under the list says so.
  const tabAdds = combo.open && combo.query.trim() !== '' && combo.active >= 0 && combo.active < options.length && !chosen.has(options[combo.active].employeeId);

  /** A mouse click on a row: add that person (Shift: everyone between the anchor and the row). The list stays open, the typed filter stays, focus stays in the input. */
  function pick(employeeId: string, index: number, shift: boolean): void {
    const decision = decidePick(options.map((o) => o.employeeId), selectedIds, anchor, employeeId, shift);
    for (const personId of decision.toAdd) onAdd(personId);
    setAnchor(decision.anchor);
    setCombo((state) => afterMousePick(state, index));
    inputRef.current?.focus();
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (disabled) return;
    if (event.shiftKey && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      const extend = decideExtend(event.key, combo, options, anchor, selectedIds);
      if (extend.handled) event.preventDefault();
      for (const personId of extend.toAdd) onAdd(personId);
      if (extend.anchor !== anchor) setAnchor(extend.anchor);
      if (extend.state !== combo) setCombo(extend.state);
      return;
    }
    // Tab (Owner direction 2026-10-20 night; only the people lists switch this on): with text typed and a name highlighted, decideKey chooses that person and
    // clears the field, and the key is still NOT handled (no preventDefault), so focus moves on to the next field as usual. Shift+Tab, a Tab with nothing typed or
    // nothing matching, and a Tab on a person already chosen only close the list and move on. A person is otherwise added by a click on a row, by Enter on the
    // highlighted result, or by a Shift+Arrow / Shift-click range, as before.
    const decision = decideKey(event.key, combo, options, chips.length, { tabChoosesHighlighted: true, shiftKey: event.shiftKey, chosenIds: selectedIds });
    if (decision.handled) event.preventDefault();
    if (decision.stop) event.stopPropagation();
    if (decision.removeLast) {
      const last = chips[chips.length - 1];
      if (last) onRemove(last.employeeId);
      return;
    }
    if (decision.choose !== null) {
      onAdd(decision.choose);
      // Tab leaves the field at once, so the person who was added is named in the polite status line (Enter keeps focus in the field and has never said it).
      if (event.key === 'Tab') setAnnouncement(describeTabAdd(optionText(choiceById.get(decision.choose), options.find((o) => o.employeeId === decision.choose)?.displayName ?? '').short));
    }
    if (decision.state !== combo) setCombo(decision.state);
  }

  const showList = combo.open && !disabled;
  return (
    <div ref={wrapRef} className="relative">
      <div
        className={`flex flex-wrap items-center gap-1 rounded border bg-white px-2 py-1 focus-within:ring-2 focus-within:ring-[#4AABDE] ${invalid ? 'border-red-500' : 'border-gray-300'} ${disabled ? 'opacity-60' : ''}`}
        onMouseDown={(event) => {
          // A click on the empty part of the box puts the cursor in the field; a click on a chip's button or the field itself keeps its own behavior.
          if (event.target === event.currentTarget && !disabled) {
            event.preventDefault();
            inputRef.current?.focus();
          }
        }}
      >
        {leadingIcon === 'search' && (
          <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="ml-1 h-4 w-4 flex-shrink-0 text-gray-400" data-testid={`${id}-search-icon`}>
            <circle cx="11" cy="11" r="7" />
            <path d="M20 20l-3.5-3.5" />
          </svg>
        )}
        {chips.length > 0 && (
          <ul className="contents" aria-label="Chosen people" data-testid={`${id}-chips`}>
            {chips.map((chip) => (
              <ChosenChip
                key={chip.employeeId}
                name={chip.displayName}
                department={choiceById.get(chip.employeeId)?.department}
                tag={youTag(chip.employeeId)}
                disabled={disabled}
                onRemove={() => {
                  onRemove(chip.employeeId);
                  inputRef.current?.focus();
                }}
              />
            ))}
          </ul>
        )}
        <input
          ref={inputRef}
          id={id}
          type="text"
          role="combobox"
          aria-labelledby={labelledBy}
          aria-expanded={showList}
          aria-controls={listboxId}
          aria-haspopup="listbox"
          aria-autocomplete="list"
          aria-activedescendant={showList ? view.activeDescendant : undefined}
          aria-describedby={[describedBy, showList ? hintId : undefined].filter(Boolean).join(' ') || undefined}
          aria-invalid={invalid || undefined}
          aria-required={required || undefined}
          data-autofocus={wantsFocus ? '' : undefined}
          autoComplete="off"
          disabled={disabled}
          value={combo.query}
          placeholder={placeholderFor(chips.length, placeholder)}
          onChange={(event) => setCombo(typeQuery(combo, event.target.value))}
          onKeyDown={onKeyDown}
          onBlur={(event) => {
            // Moving to the "Show people" button or into the list keeps it open; leaving the field closes it.
            const next = event.relatedTarget;
            if (!(next instanceof Node && wrapRef.current !== null && wrapRef.current.contains(next))) setCombo((state) => closeList(state));
          }}
          className="min-w-[10rem] flex-1 border-0 bg-transparent px-1 py-1 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0"
        />
        <button
          type="button"
          tabIndex={-1}
          disabled={disabled}
          aria-label={showList ? 'Hide the list of people' : 'Show the list of people'}
          title={showList ? 'Hide the list of people' : 'Show the list of people'}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => {
            setCombo((state) => clampActive(toggleList(state), searchPeople(listOf, state.query).length));
            inputRef.current?.focus();
          }}
          // Owner edit: the arrow is a 24px chevron (was a 10px text glyph) in a 40 x 40 px hit area (was 24 x 24). The negative margins let the button use the
          // box's own padding, so the field grows by 4px at most instead of 12.
          className="-my-1 -mr-1 ml-auto flex h-10 w-10 flex-shrink-0 items-center justify-center rounded text-gray-700 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4AABDE]"
        >
          <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
      </div>

      <div role="status" aria-live="polite" className="sr-only">
        {view.status !== '' ? view.status : announcement}
      </div>

      {showList && (
        // The wrapper keeps the pointer from taking focus out of the input (a click on the hint must not close the list). The hint sits OUTSIDE the listbox
        // (a listbox holds only options) and below the scrolling rows, so it is always visible.
        <DropdownShell floating={floating} anchorRef={wrapRef}>
          <ul id={listboxId} role="listbox" aria-label="People" aria-multiselectable="true" className="max-h-56 overflow-y-auto py-1" data-testid={`${id}-listbox`}>
            {options.map((person, index) => {
              const isChosen = chosen.has(person.employeeId);
              // D1: the short name, the department on a quiet second line (with the full name or an identifier where two people would look alike); the option is NAMED
              // with the full name and the department, and the full name is also its hover text.
              const label = optionText(choiceById.get(person.employeeId), person.displayName);
              const optionName = `${label.accessible}${youTag(person.employeeId)}`;
              const circle = circleById.get(person.employeeId);
              return (
                <li
                  key={person.employeeId}
                  id={optionId(id, person.employeeId)}
                  role="option"
                  aria-selected={isChosen}
                  aria-label={optionName}
                  title={optionName}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={(event) => pick(person.employeeId, index, event.shiftKey)}
                  onMouseMove={() => setCombo((state) => (state.active === index ? state : { ...state, active: index }))}
                  className={`flex cursor-pointer select-none items-center gap-2 px-3 py-1.5 text-sm text-gray-900 ${index === combo.active ? 'bg-sky-100' : isChosen ? 'bg-sky-50 hover:bg-sky-100' : 'hover:bg-gray-50'}`}
                  data-chosen={isChosen ? 'true' : undefined}
                >
                  <span aria-hidden="true" className="w-4 flex-shrink-0 text-center text-[#1f6a99]">
                    {isChosen ? '\u2713' : ''}
                  </span>
                  {circle !== undefined && <AvatarCircle badge={circle} size="compact" />}
                  <span className="min-w-0">
                    <span className="block">
                      {label.short}
                      {youTag(person.employeeId)}
                    </span>
                    {label.detail !== '' && <span className="block text-xs leading-tight text-gray-500">{label.detail}</span>}
                  </span>
                </li>
              );
            })}
            {options.length === 0 && (
              <li role="presentation" className="px-3 py-2 text-sm text-gray-500">
                {people.kind === 'loading' ? 'Loading people…' : combo.query.trim() === '' ? 'No more people to choose.' : 'No people match.'}
              </li>
            )}
          </ul>
          <p id={hintId} className="border-t border-gray-100 px-3 py-1 text-xs text-gray-600" data-testid={`${id}-hint`}>
            {RANGE_HINT}
            {tabAdds ? ' Tab adds the highlighted name.' : ''}
          </p>
        </DropdownShell>
      )}

      {people.kind === 'error' && (
        <p role="alert" className="mt-1 text-xs text-red-700">
          {people.heading}
          {people.detail !== '' ? ` ${people.detail}` : ''}{' '}
          <button type="button" onClick={onRetry} aria-label="Retry loading people" className="underline">
            Retry
          </button>
        </p>
      )}
      {people.kind === 'empty' && <p className="mt-1 text-xs text-gray-600">{people.text}</p>}
      {people.kind === 'loading' && !showList && (
        <p className="mt-1 text-xs text-gray-500" role="status">
          {people.text}
        </p>
      )}
    </div>
  );
}
